import { useEffect } from "react";
import styles from "./CreateCommunityPage.module.css";
import api from "../../services/api";
import { ENDPOINTS } from "../../utils/endpoints";
import useDashboardStore from "../../store/dashboardStore";
import { toast } from "react-toastify";

// Components
import CommunityImageUpload from "../../components/Community/CommunityImageUpload";
import CommunityBasicInfo from "../../components/Community/CommunityBasicInfo";
import SectionsManager from "../../components/Community/SectionsManager";
import CommunityContent from "../../components/Community/CommunityContent";
import VideoTabsManager from "../../components/VideoTabsManager";

// Hooks
import { useCommunityForm } from "../../hooks/useCommunityForm";
import { useSections } from "../../hooks/useSections";
import { useVideoTabs } from "../../hooks/useVideoTabs";

const CreateCommunityPage = () => {
  const { setIsAllCommunityScreen, editCommunityId } = useDashboardStore();

  // Custom hooks
  const communityFormState = useCommunityForm(editCommunityId);
  const sectionsState = useSections();
  const videoTabsState = useVideoTabs(editCommunityId);

  // Load video tabs when editing community
  useEffect(() => {
    const loadCommunityData = async () => {
      if (editCommunityId !== null) {
        try {
          const communityData = await communityFormState.prefillFormForEdit();

          if (communityData) {
            // Set sections
            sectionsState.setSelectedSectionIds(
              communityData.section_ids || [],
            );

            // Load video tabs
            const tabs = communityData.video_tabs || [];
            const loadedTabs = tabs.map((tab) => ({
              ...tab,
              groups: (tab.groups || []).map((group) => ({
                ...group,
                group_banner_mobile_url: group.group_banner_mobile_url
                  ? ENDPOINTS.IMAGE_BASE_URL + group.group_banner_mobile_url
                  : null,
                group_banner_web_url: group.group_banner_web_url
                  ? ENDPOINTS.IMAGE_BASE_URL + group.group_banner_web_url
                  : null,
                videos: (group.videos || []).map((video) => ({
                  ...video,
                  cover_image: video.cover_image
                    ? ENDPOINTS.IMAGE_BASE_URL + video.cover_image
                    : null,
                  attachment_file: null,
                  attachment_file_url: video.attach_file
                    ? ENDPOINTS.IMAGE_BASE_URL + video.attach_file
                    : null,
                })),
              })),
            }));

            videoTabsState.setVideoTabs(loadedTabs);
          }
        } catch (err) {
          console.error("Failed to load community data", err);
        }
      }
    };

    loadCommunityData();
  }, [editCommunityId]);

  const handleSubmit = async () => {
    if (communityFormState.isSubmitting) return;

    if (
      !communityFormState.communityName ||
      !communityFormState.themeColor ||
      !sectionsState.selectedSectionIds.length
    ) {
      toast.error("Please fill all required fields.");
      return;
    }

    if (editCommunityId === null) {
      if (!communityFormState.bannerFile || !communityFormState.iconFile) {
        toast.error("Please upload both banner and icon images.");
        return;
      }
    }

    communityFormState.setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("name", communityFormState.communityName);
      formData.append("theme_color", communityFormState.themeColor);
      formData.append(
        "has_live_session",
        communityFormState.hasLiveSession === true ? true : false,
      );
      formData.append(
        "section_ids",
        JSON.stringify(sectionsState.selectedSectionIds),
      );

      if (communityFormState.bannerFile)
        formData.append("banner", communityFormState.bannerFile);
      if (communityFormState.iconFile)
        formData.append("icon", communityFormState.iconFile);

      const content = communityFormState.content;

      if (content.communityTitle)
        formData.append("community_title", content.communityTitle);
      if (content.contentTitle)
        formData.append("content_title", content.contentTitle);
      if (content.primaryText)
        formData.append("content_primary_text", content.primaryText);
      if (content.secondaryText)
        formData.append("content_secondary_text", content.secondaryText);

      // Web/App images
      if (content.deleteContentBannerWeb)
        formData.append("delete_content_banner_web", "true");
      if (content.deleteContentBannerApp)
        formData.append("delete_content_banner_app", "true");
      if (content.deleteContentImageWeb)
        formData.append("delete_content_image_web", "true");
      if (content.deleteContentImageApp)
        formData.append("delete_content_image_app", "true");

      if (content.contentBannerWebFile)
        formData.append("content_banner_web", content.contentBannerWebFile);
      if (content.contentBannerAppFile)
        formData.append("content_banner_app", content.contentBannerAppFile);
      if (content.contentImageWebFile)
        formData.append("content_image_web", content.contentImageWebFile);
      if (content.contentImageAppFile)
        formData.append("content_image_app", content.contentImageAppFile);

      // Append all video cover files
      videoTabsState.videoCoverFiles.forEach((file) => {
        formData.append("video_covers", file);
      });

      let attachments = [];
      let groupBanners = [];
      (videoTabsState?.videoTabs || []).forEach((tab) => {
        (tab?.groups || []).forEach((group) => {
          if (group?.group_banner_mobile_file) {
            groupBanners.push(group.group_banner_mobile_file);
          }
          if (group?.group_banner_web_file) {
            groupBanners.push(group.group_banner_web_file);
          }

          (group?.videos || []).forEach((video) => {
            if (video?.attachment_file) {
              attachments.push(video.attachment_file);
            }
          });
        });
      });

      // Append all attachment files
      attachments.forEach((file) => {
        formData.append("video_attachments", file);
      });

      // Append all group banner files
      groupBanners.forEach((file) => {
        formData.append("group_files", file);
      });

      // Video tabs structure
      if (videoTabsState.videoTabs.length > 0) {
        const sortedTabs = videoTabsState.videoTabs.map((tab, tabIdx) => ({
          ...tab,
          sort_order: tabIdx,
          groups: (tab.groups || []).map((group, groupIdx) => {
            // Mobile
            let cleanGroupMobileUrl = group.group_banner_mobile_url;
            if (cleanGroupMobileUrl && cleanGroupMobileUrl.includes("/uploads/")) {
              const parts = cleanGroupMobileUrl.split("/uploads/");
              cleanGroupMobileUrl = parts[parts.length - 1];
            }

            let group_banner_mobile_index = null;
            if (group.group_banner_mobile_file) {
              const index = groupBanners.findIndex(
                (f) => f?.name === group.group_banner_mobile_file?.name &&
                  f?.size === group.group_banner_mobile_file?.size
              );
              group_banner_mobile_index = index !== -1 ? index : null;
            }

            // Web
            let cleanGroupWebUrl = group.group_banner_web_url;
            if (cleanGroupWebUrl && cleanGroupWebUrl.includes("/uploads/")) {
              const parts = cleanGroupWebUrl.split("/uploads/");
              cleanGroupWebUrl = parts[parts.length - 1];
            }

            let group_banner_web_index = null;
            if (group.group_banner_web_file) {
              const index = groupBanners.findIndex(
                (f) => f?.name === group.group_banner_web_file?.name &&
                  f?.size === group.group_banner_web_file?.size
              );
              group_banner_web_index = index !== -1 ? index : null;
            }

            return {
              ...group,
              sort_order: groupIdx,
              group_banner_mobile_url: cleanGroupMobileUrl,
              group_banner_mobile_index: group_banner_mobile_index,
              group_banner_web_url: cleanGroupWebUrl,
              group_banner_web_index: group_banner_web_index,
              
              videos: (group.videos || []).map((video, videoIdx) => {
                // Clean the cover_image URL - extract only the filename
                let cleanCoverImage = video.cover_image;
                if (cleanCoverImage && cleanCoverImage.includes("/uploads/")) {
                  const parts = cleanCoverImage.split("/uploads/");
                  cleanCoverImage = parts[parts.length - 1];
                }

                let cleanAttachedFileUrl = video.attachment_file_url;
                if (cleanAttachedFileUrl && cleanAttachedFileUrl.includes("/uploads/")) {
                  const parts = cleanAttachedFileUrl.split("/uploads/");
                  cleanAttachedFileUrl = parts[parts.length - 1];
                }

                let attach_file_index = null;
                if (video.attachment_file) {
                  const index = attachments.findIndex(
                    (f) => f?.name === video.attachment_file?.name &&
                      f?.size === video.attachment_file?.size
                  );
                  attach_file_index = index !== -1 ? index : null;
                }

                return {
                  ...video,
                  sort_order: videoIdx,
                  cover_image: cleanCoverImage,
                  attach_file: cleanAttachedFileUrl,
                  attach_file_index: attach_file_index,
                };
              }),
            }
          }),
        }));
        formData.append("video_tabs", JSON.stringify(sortedTabs));
      }

      const { data } =
        editCommunityId !== null
          ? await api.put(
            ENDPOINTS.UPDATE_COMMUNITY_BY_ID(editCommunityId),
            formData,
          )
          : await api.post(ENDPOINTS.CREATE_COMMUNITY, formData);

      if (data.success) {
        toast.success(
          data.message ||
          `Community ${editCommunityId ? "updated" : "created"} successfully`,
        );
        setIsAllCommunityScreen();
      } else {
        toast.error(data.message || "Something went wrong");
      }
    } catch (err) {
      console.error(err);
      toast.error(
        `Error ${editCommunityId ? "updating" : "creating"} community`,
      );
    } finally {
      communityFormState.setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.pageWrapper}>
      {/* App Bar */}
      <div className={styles.appbarContainer}>
        <h2 className={styles.appbarTitle}>
          {editCommunityId === null ? "Create" : "Update"} Community
        </h2>
      </div>

      <div className={styles.bodyContainer}>
        {/* Left Section */}
        <div className={styles.leftSection}>
          <CommunityImageUpload
            bannerPreview={communityFormState.bannerPreview}
            iconPreview={communityFormState.iconPreview}
            handleBannerUpload={communityFormState.handleBannerUpload}
            handleIconUpload={communityFormState.handleIconUpload}
            isSubmitting={communityFormState.isSubmitting}
          />

          <CommunityBasicInfo
            communityName={communityFormState.communityName}
            setCommunityName={communityFormState.setCommunityName}
            themeColor={communityFormState.themeColor}
            setThemeColor={communityFormState.setThemeColor}
            hasLiveSession={communityFormState.hasLiveSession}
            setHasLiveSession={communityFormState.setHasLiveSession}
            isSubmitting={communityFormState.isSubmitting}
          />

          <SectionsManager
            filteredSections={sectionsState.filteredSections}
            searchText={sectionsState.searchText}
            setSearchText={sectionsState.setSearchText}
            newSection={sectionsState.newSection}
            setNewSection={sectionsState.setNewSection}
            selectedSectionIds={sectionsState.selectedSectionIds}
            editingSectionId={sectionsState.editingSectionId}
            editingSectionTitle={sectionsState.editingSectionTitle}
            setEditingSectionTitle={sectionsState.setEditingSectionTitle}
            isCreatingSection={sectionsState.isCreatingSection}
            isRenamingSection={sectionsState.isRenamingSection}
            deletingSectionId={sectionsState.deletingSectionId}
            isSubmitting={communityFormState.isSubmitting}
            handleCheckboxChange={sectionsState.handleCheckboxChange}
            handleCreateSection={sectionsState.handleCreateSection}
            handleStartEdit={sectionsState.handleStartEdit}
            handleCancelEdit={sectionsState.handleCancelEdit}
            handleSaveRename={sectionsState.handleSaveRename}
            handleDeleteSection={sectionsState.handleDeleteSection}
          />
        </div>

        {/* Right Section */}
        <div className={styles.rightSection}>
          <CommunityContent
            content={communityFormState.content}
            setContent={communityFormState.setContent}
            isSubmitting={communityFormState.isSubmitting}
          />

          <VideoTabsManager
            videoTabsState={videoTabsState}
            isSubmitting={communityFormState.isSubmitting}
          />

          {/* Submit Button */}
          <button
            className={`${styles.submitButton} ${communityFormState.isSubmitting ? styles.submitting : ""
              }`}
            onClick={handleSubmit}
            disabled={communityFormState.isSubmitting}
          >
            {communityFormState.isSubmitting ? (
              <>
                <span>
                  {editCommunityId === null ? "Creating..." : "Updating..."}
                </span>
                <span className={styles.submitIcon}>⟳</span>
              </>
            ) : (
              <>
                <span>Submit Community</span>
                <span className={styles.submitIcon}>＋</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateCommunityPage;
